//Nov4 AssetBoard.jsx:
//may7 AssetBoard without name check logic:
// src/AssetManager/AssetBoard.jsx
import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Dialog,
  Chip,
} from '@mui/material';
import AssetCard          from './AssetCard';
import AddIcon            from '@mui/icons-material/Add';
//import AssetUploadDialog  from './AssetUploadDialog';
import { fetchAssets }    from '../utils/frontend_assetManager';
import { useAuth }        from '../contexts/AuthContext';
import { auth }           from '../firebase';
import './styles/assetManager.css';
import { getThumbAspect} from '../utils/assetLayout';
import useJustifiedRows from '../hooks/useJustifiedRows';


/**
 * AssetBoard
 * ---------------------------------------------------------------------------
 * Renders a grid of AssetCards with **infinite scroll**.
 * Exposes refresh() via ref so a parent (e.g. AssetLibrary) can force
 * a re-query after uploads.
 */
const AssetBoard = forwardRef(({
  isModal      = false,
  onClose,
  boardId,
  categoryId,
  onSelectAsset,
  onUploadRequest,
  initialFormId = null,
  // server-side search term (debounced in parent if needed)
  searchQuery = '',
  // promoted filters from AssetLibrary / MFP
  promotedFilters = { equals: {}, equalsAny: {}, arrayAny: {}, dateRange: {} },

  // Legacy fallback for basic-plan orgs (no AUF / no catalog).
  // AssetLibrary will pass this only when assetTypeUfi is unavailable.
  assetTypeFilter = null,

  onDetails = undefined,

  // scroll root ref (AssetLibrary’s .asset-library-content)
  // If provided, IntersectionObserver will use this element as its root.
  scrollRootRef = null,
}, ref) => {



  /* ───────────── context ───────────── */
  const { organizationId, userPermissions, getToken } = useAuth();

  /* ───────────── state ───────────── */
  const [assets,       setAssets]       = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [booted, setBooted] = useState(false); // first fetch attempted
 
  const [currentPage,  setCurrentPage]  = useState(1);
  const [lastVisibleId,setLastVisibleId]= useState(null);
  const [totalPages,   setTotalPages]   = useState(0);
  const [hasMore,      setHasMore]      = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const [tagsFilter,   setTagsFilter]   = useState([]); // free-form tags
  const [formIdFilter, setFormIdFilter] = useState(initialFormId);

  // View settings (can become user prefs later)
  const [viewSettings] = useState({
    heroTiles: true,      // allow span-2 wide tiles
    density  : 'default', // 'compact' | 'default' | 'comfortable'
  });

  const pageSize    = 12;
  const sentinelRef = useRef(null);    // intersection sentinel

  // Pause board refresh/paging while the asset details dialog is open
  const [dialogOpen, setDialogOpen] = useState(false);
  const dialogOpenRef = useRef(false);
  useEffect(() => {
    const onDialog = (e) => {
      const open = !!e?.detail?.open;
      setDialogOpen(open);
      dialogOpenRef.current = open;
    };
    window.addEventListener('assetdetails:dialog-open', onDialog);
    return () => window.removeEventListener('assetdetails:dialog-open', onDialog);
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Global delete sync: if any card deletes an asset, remove it
  // from this board immediately to prevent "ghost" clickable tiles.
  // (AssetCard dispatches: window.dispatchEvent(new CustomEvent('asset:deleted', ...))
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const onDeleted = (e) => {
      const id = e?.detail?.assetId || null;
      if (!id) return;

      setAssets((prev) =>
        (prev || []).filter((a) => (a.id || a.assetId) !== id)
      );

      // Also, if our cursor is pointing at the deleted doc, clear it
      // so the next fetch doesn't try to startAfter a missing doc.
      setLastVisibleId((prev) => (prev === id ? null : prev));
    };

    window.addEventListener('asset:deleted', onDeleted);
    return () => window.removeEventListener('asset:deleted', onDeleted);
  }, []);

  // ─────────────────────────────────────────────────────────────
  // optimistic delete recovery
  // If the backend delete fails, the card was already removed from UI.
  // Reconcile by reloading the current list slice from source-of-truth.
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const onDeleteFailed = (e) => {
      const id = e?.detail?.assetId || null;
      if (!id) return;

      console.warn('[AssetBoard] Delete failed → reconciling list:', id);

      // Single source of truth: do the same reset+fetch path as refresh()
      // (avoids double state churn + avoids touching the forwarded `ref` param)
      setAssets([]);
      setCurrentPage(1);
      setLastVisibleId(null);
      setHasMore(true);
      fetchPaginatedAssets(1);
    };

    window.addEventListener('asset:delete-failed', onDeleteFailed);
    return () =>
      window.removeEventListener('asset:delete-failed', onDeleteFailed);
  }, []);


  // --- grid dimensions (keep in one place) --------------------------
  const TRACK_W = 200;   // single-card width  ➜ matches gridTemplateColumns
  const GAP_W   = 12;    // gap between tracks ➜ matches `gap`

  // Stable dependency key for promoted filters (avoids ref-only changes)
  const promotedKey = JSON.stringify(promotedFilters || {});


  // ───────────────── Justified layout inputs ─────────────────
  // Map assets → { id, aspect, data } for the layout engine.
  const items = useMemo(() => {
    return (assets || []).map((a) => ({
      id: a.id || a.assetId,
      aspect: getThumbAspect(a) || 1,
      data: a,
    }));
  }, [assets]);

  // Choose a target row height based on your density preference.
  const targetRowH =
    viewSettings.density === 'compact' ? 170 :
    viewSettings.density === 'comfortable' ? 230 : 200;

  // Compute justified geometry (absolute positions & container height).
 const { containerRef, containerWidth, layout, ready } = useJustifiedRows(items, {
    targetRowHeight: targetRowH,
    boxSpacing: { horizontal: GAP_W, vertical: GAP_W }, // keep your 24px gap
    containerPadding: 0,
    showWidows: true,   // ragged last row for a clean look
  });

  // DEBUG: prevent console storms (logs only when key metrics change)
  // ENTERPRISE: gate behind a runtime flag so production console stays clean.
  // Enable in DevTools when needed:
  //   window.__DEBUG_JL__ = true
  const jlLogKey = `${containerWidth}|${layout?.boxes?.length ?? 0}|${Math.round(layout?.containerHeight ?? 0)}|${assets.length}|${ready}`;
  const jlLastRef = useRef('');
  if (jlLastRef.current !== jlLogKey) {
    jlLastRef.current = jlLogKey;

    const __DBG_JL__ =
      typeof window !== 'undefined' && window.__DEBUG_JL__ === true;

    if (__DBG_JL__) {
      console.log('[JL]', {
        hookWidth: containerWidth,  // was: layout ? : 'n/a'
        domWidth: containerRef.current?.getBoundingClientRect?.().width ?? '(no ref)',
        boxes: layout?.boxes?.length ?? 0,
        height: layout?.containerHeight ?? 0,
        assets: assets.length,
        ready,
      });
    }
  }




  // ------------------------------------------------------------------
  //  De-dupe helper – keeps just ONE copy of every assetId
  //  (used both when we append a newly-fetched page and when we prepend
  //   the freshly-uploaded asset so React never sees duplicate keys).
  // ------------------------------------------------------------------
  const dedupeById = (arr) => {
    const map = new Map();          // Map<uniqueKey, asset>
    arr.forEach((a) => {
      const key =
        a.id && a.id.trim() !== ''  // prefer a real “id” if present & non-empty
          ? a.id
          : a.assetId;              // otherwise fall back to assetId
      if (key) map.set(key, a);     // ignore rows missing BOTH
    });
    return [...map.values()];
  };


  /* ───────────── data fetch ───────────── */
  const fetchPaginatedAssets = async (page) => {
    // Hard pause while dialog is open (prevents fetch churn + layout flickers)
    if (dialogOpenRef.current) {
      console.log('[AssetBoard] Paused fetch (dialog open). Skipping page', page);
      return;
    }

    if (!organizationId) {
      console.warn('[AssetBoard] organizationId is undefined.');
      return;
    }

    if (!userPermissions.includes('viewAsset')) {
      setErrorMessage('You do not have permission to view assets.');
      console.error('[AssetBoard] Missing permission: viewAsset');
      return;
    }

    try {
      setLoading(true);
      if (!booted) setBooted(true);

      setErrorMessage('');
      console.log(`[AssetBoard] Fetching assets page ${page}`, {
        tagsFilter,
        formIdFilter,
        searchQuery,
        promotedFilters, // keep the full object (useful while developing)
        // Compact summary so logs stay readable:
        promotedSummary: {
          equals   : Object.keys(promotedFilters?.equals    || {}).length,
          equalsAny: Object.keys(promotedFilters?.equalsAny || {}).length,
          arrayAny : Object.keys(promotedFilters?.arrayAny  || {}).length,
          dateRange: Object.keys(promotedFilters?.dateRange || {}).length,
        },
      });



      const authToken = await getToken?.();
      if (!authToken) {
        throw new Error('[AssetBoard] Failed to retrieve auth token.');
      }

      const t0 = performance.now();
      console.log('[AssetBoard] assetTypeFilter:', assetTypeFilter);

      const response = await fetchAssets(
        organizationId,
        null,                           // collectionId (add later if needed)
        tagsFilter,                     // tags
        searchQuery,                    // server-side search term
        pageSize,
        page === 1 ? null : lastVisibleId,   // ← string cursor
        authToken,
        formIdFilter,
        assetTypeFilter,                // assetType (legacy/basic fallback)
        true,                           // includeUrls (keep true for thumbs)
        false,                          // includeCount: skip total for fast paint
        promotedFilters                 // full promoted selections
      );


console.log('[Board] fetchAssets ms =', Math.round(performance.now() - t0));

      console.log('[AssetBoard] Fetched assets:', response);
      // If the dialog opened while the request was in-flight, drop results silently
      if (dialogOpenRef.current) {
        console.log('[AssetBoard] Dropping results (dialog opened mid-request).');
        return;
      }


        if (response.assets.length > 0) {
    setAssets(prev => {
      if (page === 1) {
        const normalized = (response.assets || []).map(a =>
          a.id ? a : { ...a, id: a.assetId || a.id }
        );
        return normalized;
      }


      const normalized = (response.assets || []).map(a =>
        a.id ? a : { ...a, id: a.assetId || a.id }
      );
      const merged  = [...prev, ...normalized];
      const deduped = dedupeById(merged);


          // If length didn’t grow, the “next” page was a repeat ➜ stop here.
          if (deduped.length === prev.length) {
            console.warn('[AssetBoard] Duplicate page; stopping further loads');
            setHasMore(false);
          }

          return deduped;
        });

        setLastVisibleId(response.lastVisibleId);

        // Keep your existing math/logs, but drive hasMore from cursor.
        const newTotalPages = Math.ceil(response.totalCount / pageSize);
        setTotalPages(newTotalPages);

        // NEW: prioritize backend cursor for infinite scroll
        setHasMore(Boolean(response.lastVisibleId));

        // Defensive: if fewer than pageSize came back, likely last page.
        if ((response.assets || []).length < pageSize) {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
        console.log('[AssetBoard] No more assets to load.');
      }


      setCurrentPage(page);
    } catch (err) {
      console.error('[AssetBoard] Error fetching assets:', err.message);
      setErrorMessage('Failed to fetch assets. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /* ───────────── imperative refresh() exposed to parent ───────────── */
  useImperativeHandle(ref, () => ({
    refresh() {
      console.log('[AssetBoard] refresh() called from parent.');
      setAssets([]);
      setCurrentPage(1);
      setLastVisibleId(null);
      setHasMore(true);
      fetchPaginatedAssets(1);
    },
    prependAsset(newAsset) {
      const norm = { ...newAsset };
      if (!norm.id && norm.assetId) norm.id = norm.assetId;
      setAssets(prev => {
        /* ----------------------------------------------------------
           1️⃣  Drop any older version of that canonical file
           2️⃣  Ensure no duplicate keys via de-dupe
        ---------------------------------------------------------- */
        const withoutOlderVersion = prev.filter(a =>
          (a.canonicalName ?? '').toLowerCase() !==
            (norm.canonicalName ?? '').toLowerCase() ||
          a.assetType !== norm.assetType
        );
        return dedupeById([norm, ...withoutOlderVersion]);
      });

    },
    /* NEW – remove a card by id (used by AssetLibrary after replace) */
    removeAssetById(id) {
      if (!id) return;
      setAssets(prev => prev.filter(a => (a.id || a.assetId) !== id));
    },
    
    /* update one card in place (used after worker finalizes) */
    updateAssetById(id, next) {
      if (!id || !next) return;
      setAssets(prev =>
        prev.map(a => ( (a.id || a.assetId) === id ? { ...a, ...next } : a ))
      );
    },


    /*  – allow parent to clear form filter */
    clearFormFilter() {
      setFormIdFilter(null);
      console.log('[AssetBoard] Form filter cleared.');
      this.refresh();
    },
  }));

  /* ───────────── initial / organization change ───────────── */
  useEffect(() => {
    console.log('[AssetBoard] promotedFilters changed →', promotedFilters);

    // Skip reloads while the details dialog is open
    if (dialogOpen) {
      console.log('[AssetBoard] Reload skipped (dialog open).');
      return;
    }

    if (organizationId) {
      console.log('[AssetBoard] Initial/org/filter change — reloading.', {
        tagsFilter,
        formIdFilter,
        searchQuery,
        promotedFilters,
      });
      setAssets([]);
      setCurrentPage(1);
      setLastVisibleId(null);
      setHasMore(true);
      fetchPaginatedAssets(1);
    }
    // include filters to auto-refresh when they change + dialogOpen (pause)
  }, [organizationId, tagsFilter, formIdFilter, searchQuery, promotedKey, dialogOpen]);



  /* ───────────── IntersectionObserver for infinite scroll ───────────── */
  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    if (dialogOpen) return; // do not attach observer while dialog is open

    // observe within the actual scroll container when provided
    const rootEl = scrollRootRef?.current || null;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loading && !dialogOpenRef.current) {
          console.log('[AssetBoard] Sentinel intersected — fetching next page.');
          fetchPaginatedAssets(currentPage + 1);
        }
      },
      { root: rootEl, rootMargin: '200px' }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, currentPage, dialogOpen, scrollRootRef]);



  /* ───────────── quick filter-chip UI (optional) ───────────── */
  const renderFilterChips = () => (
    <Box sx={{ display:'flex', gap:1, px:1, pb:1 }}>
      {formIdFilter && (
        <Chip
          size="small"
          label="This form"
          onDelete={() => setFormIdFilter(null)}
        />
      )}
      {tagsFilter.map((t) => (
        <Chip
          key={t}
          size="small"
          label={t}
          onDelete={() =>
            setTagsFilter(prev => prev.filter(tag => tag !== t))}
        />
      ))}
    </Box>
  );

  // ─────────────────────────────────────────────────────────────
  // PURE render helpers (no global writes).
  // These replace window.__eagerCount and window.__firstTwoRowTops.
  // ─────────────────────────────────────────────────────────────

  const eagerCount = useMemo(() => {
    // Estimate columns from measured container width and track width
    const estCols = Math.max(1, Math.floor((containerWidth || 0) / TRACK_W));
    const safeCols = Math.max(1, Math.min(estCols, 8));
    return safeCols * 2;
  }, [containerWidth]);

  const firstTwoRowTops = useMemo(() => {
    const boxes = layout?.boxes || [];
    const rowTops = [];
    for (const b of boxes) {
      const t = Math.round(b.top);
      if (!rowTops.includes(t)) rowTops.push(t);
      if (rowTops.length >= 2) break;
    }
    return new Set(rowTops.slice(0, 2));
  }, [layout?.boxes]);

  // ───────────────── remove-on-delete bridge ─────────────────
  // When an AssetCard calls onDetails({ deleted: true, assetId? }),
  // the card also dispatches a global 'asset:deleted' event.
  // We keep AssetBoard removal canonical via the global event listener above
  // to avoid duplicate state updates / render churn.
  const handleCardDetails = (asset, info) => {
    // bubble up for any external listeners
    try { onDetails?.(info, asset); } catch (_) {}
  };



  /* ───────────── justified renderer ───────────── */
  const renderJustified = () => {
    const hasBoxes = (layout?.boxes?.length ?? 0) > 0;
    // Mount the measured container ALWAYS so the hook can measure width asap
    return (
      <Box
        ref={containerRef}
        sx={{
          px: { xs: '8px', sm: '12px', md: '16px', lg: '24px' },
          width: '100%',
        }}
      >
        {/* Fallback grid when there are assets but layout produced 0 boxes */}
        {!hasBoxes && assets.length > 0 ? (
         <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: `${GAP_W}px`,
            }}
          >
            {assets.map((asset, idx) => (
              <AssetCard
                key={asset.id || asset.assetId}
                asset={asset}
                overrideAspect={getThumbAspect(asset) || 1}
                forceContain
                renderWidth={200}   // grid column size hint
                loadingPriority={idx < eagerCount ? 'high' : 'auto'}
                sx={{ width: '100%' }}
                onDetails={(info) => handleCardDetails(asset, info)}
                onClick={() => {
                  const norm = asset.id ? asset : { ...asset, id: asset.assetId };
                  onSelectAsset?.(norm);
                }}
              />
            ))}
          </Box>
          </>

        ) : (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: `${Math.max(0, Math.round(layout.containerHeight || 0))}px`,
            }}
          >
           {/* First two row tops → eager/high fetch priority (memoized: firstTwoRowTops) */}
            
            {layout.boxes.map((box) => {
              const asset = box.data;
              return (
                <Box
                  key={box.id}
                  className="jl-cell"
                  sx={{
                    position: 'absolute',
                    left: `${Math.round(box.left)}px`,
                    top: `${Math.round(box.top)}px`,
                    width: `${Math.round(box.width)}px`,
                  }}
                >
                  <AssetCard
                    asset={asset}
                    overrideAspect={box.width / Math.max(1, box.height)}
                    forceContain
                    renderWidth={Math.round(box.width)}
                   loadingPriority={
                     firstTwoRowTops.has(Math.round(box.top))
                       ? 'high'
                       : 'auto'
                   }

                    sx={{ width: '100%' }}
                    onDetails={(info) => handleCardDetails(asset, info)}
                    onClick={() => {
                      const norm = asset.id ? asset : { ...asset, id: asset.assetId };
                      onSelectAsset?.(norm);
                    }}
                  />
                </Box>
              );
            })}

            {/* tiny overlay spinner while recomputing, but container stays mounted */}
            {!ready && assets.length > 0 && !loading && (
              <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}>
                <CircularProgress size={20} />
              </Box>
            )}
          </Box>
        )}
      </Box>
    );

  };


  const renderZeroState = () => (
    <Box
      sx={{
        p: 4,
        textAlign: 'center',
        display  : 'flex',
        flexDirection: 'column',
        alignItems   : 'center',
      }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        No assets found
      </Typography>
      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          if (onUploadRequest) {
            onUploadRequest();
          } else {
            console.warn('[AssetBoard] onUploadRequest prop not provided.');
          }
        }}
        startIcon={<AddIcon />}
      >
        Upload Asset
      </Button>
    </Box>
  );

  // enterprise paging footer (affordance + status + retry + fallback control)
  // This answers: “How do I know I should scroll to load more?”
  const renderPagingFooter = () => {
    // Don’t show anything until first fetch attempt has happened
    if (!booted) return null;

    // Only show once there are already assets on screen
    if (assets.length === 0) return null;

    const showSpinner = loading;
    const showError   = !!errorMessage && !loading;
    const showHasMore = hasMore && !loading && !errorMessage;
    const showEnd     = !hasMore && !loading && !errorMessage;

    return (
      <Box
        sx={{
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
        }}
      >
        {/* Optional “enterprise” progress language */}
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          Loaded {assets.length}
        </Typography>

        {showSpinner && (
          <>
            <CircularProgress size={18} />
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Loading more…
            </Typography>
          </>
        )}

        {showError && (
          <>
            <Typography variant="body2" color="error">
              Couldn’t load more.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => fetchPaginatedAssets(currentPage + 1)}
            >
              Retry
            </Button>
          </>
        )}

        {showHasMore && (
          <>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Scroll to load more
            </Typography>

            {/* Enterprise fallback: explicit control */}
            <Button
              size="small"
              variant="text"
              onClick={() => fetchPaginatedAssets(currentPage + 1)}
              disabled={loading}
            >
              Load more
            </Button>
          </>
        )}

        {showEnd && (
          <Typography variant="body2" sx={{ opacity: 0.6 }}>
            End of results
          </Typography>
        )}
      </Box>
    );
  };


  /* ───────────── main body ───────────── */
  const body = (
 <Box className="asset-board-body">
      {renderFilterChips()}
       {loading && assets.length === 0 && (
         <Box sx={{ px: { xs: 1, sm: 1.5, md: 2 }, width: '100%' }}>
           <Box sx={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:'24px' }}>
             {Array.from({ length: 10 }).map((_, i) => (
               <Box key={i} sx={{ width:'100%' }}>
                 <Box sx={{ width:'100%', aspectRatio:'1 / 1', bgcolor:'action.hover', borderRadius:1 }} />
               </Box>
             ))}
           </Box>
         </Box>
       )}


      {errorMessage && (
        <Box sx={{ p:2, textAlign:'center' }}>
          <Typography color="error">{errorMessage}</Typography>
        </Box>
      )}

      {renderJustified()}

      {!loading && booted && assets.length === 0 && !errorMessage && renderZeroState()}

      {loading && assets.length > 0 && (
        <Box sx={{ display:'flex', justifyContent:'center', mt:2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* enterprise “scroll/load more” affordance row */}
      {renderPagingFooter()}

      {/* Give sentinel some vertical space so it’s easier to hit */}
      <Box ref={sentinelRef} sx={{ height:24, width:'100%' }} />
    </Box>
  );


  /* ───────────── optional modal wrapper ───────────── */
  return isModal ? (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      aria-labelledby="asset-library-dialog"
      sx={{ minHeight:'90vh' }}
    >
      {body}
    </Dialog>
  ) : body;
});

export default AssetBoard;
